import Dexie from 'dexie'

import type { ActivityModel, IActivity } from '@src/service/model'

import Repository from './base'

const PAGE_SIZE = 50
export class ActivityRepository extends Repository<ActivityModel> {
  /**
   * Variant A of the forConversation method
   *
   * This method is the original implementation of the forConversation method
   */
  async forConversation_A(
    id: string,
    beforeId?: string,
    searchActivityId?: string,
  ): Promise<IActivity[]> {
    const result = await this.table
      .where('conversationId')
      .equals(id)
      .reverse()
      .sortBy('createdAt')
      .then((result) => {
        /**
         * If search activity id is passed in, make sure it's included in the result
         */
        if (searchActivityId) {
          const index = result.findIndex((activity) => activity.id === searchActivityId)
          return result.slice(0, index + 20)
        }
        if (beforeId) {
          const startIndex = result.findIndex((activity) => activity.id === beforeId)
          return result.slice(startIndex, startIndex + 50)
        }
        return result.slice(0, 50)
      })

    return result
  }

  /**
   * Variant B of the forConversation method
   *
   * This method is the new implementation of the forConversation method.
   *
   * Returns activities in descending order by createdAt
   */
  async forConversation_B(
    conversationId: string,
    before?: Pick<ActivityModel, 'id' | 'createdAt'>,
    searchActivityId?: string,
  ): Promise<IActivity[]> {
    if (searchActivityId) {
      const existingActivity = await this.table.get(searchActivityId)

      if (existingActivity) {
        // If the activity exists, we need to fetch the activities around it
        // to make sure that we have enough activities to render the activity
        // that was passed in as the searchActivityId parameter
        const [before, after] = await Promise.all([
          this.table
            .where('[conversationId+createdAt]')
            .between(
              [conversationId, Dexie.minKey],
              [conversationId, existingActivity.createdAt],
            )
            .reverse()
            .limit(PAGE_SIZE)
            .toArray(),

          this.table
            .where('[conversationId+createdAt]')
            .between(
              [conversationId, existingActivity.createdAt],
              [conversationId, Dexie.maxKey],
            )
            .limit(PAGE_SIZE)
            .toArray(),
        ])

        // We need to reverse the after array because the query returns the activities
        // in ascending order, but we need them in descending order
        return [...before, existingActivity, ...after.reverse()]
      }
    }

    const isFirstPage = !before

    // If we're on the first page, we use Dexie's maxKey because we don't care
    // about the upper bound of the query.
    // For all other pages, we use the createdAt of the activity that was passed
    const upperBound = isFirstPage ? Dexie.maxKey : before.createdAt

    const result: IActivity[] = await this.table
      // This index will make sure we're only querying for activities that belong
      // to the conversation that was passed in as the id parameter without having
      // to filter the results manually.
      //
      // Also, it's not obvious, but the index also sorts the results by createdAt because
      // that's what indices do.
      .where('[conversationId+createdAt]')

      // Passing `id` in both the lower and upper bound will make sure that we're only
      // querying for activities that belong to the conversation
      .between([conversationId, Dexie.minKey], [conversationId, upperBound])

      // Reverse the collection to query in descending order aka ORDER BY createdAt DESC in SQL
      .reverse()
      .limit(PAGE_SIZE)
      .toArray()

    return result
  }
}
